
import React, { useEffect, useState } from 'react';
import { useTaskData } from '../../hooks/useTaskData';
import 'react-datepicker/dist/react-datepicker.css';
import ReminderDateTimePicker from './ReminderDateTimePicker';

const ReminderMe = ({
    selectedReminderDate,
    updateSelectedReminderDate,
    note,
    handleReminderChange,
    setIsReminder,
    handleUpdateTask,
    taskItem,
    clickedBellIcon,
    setShowReminderMeDropdown,
    fixedPosition,
}) => {
    const {
        updateMyDayDate,
        updateDeadlineDate,
        updateSelectedReminderDate: updateSelectedReminderDateRedux,
    } = useTaskData();
    const [customDate, setCustomDate] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [myDayChecked, setMyDayChecked] = useState(true);
    const [deadlineChecked, setDeadlineChecked] = useState(true);

    const today = { daysToAdd: 0, time: { hours: 12, minutes: 0 } };
    const tomorrow = { daysToAdd: 1, time: { hours: 8, minutes: 0 } };
    const inTwoDays = { daysToAdd: 2, time: { hours: 8, minutes: 0 } };
    const nextWeek = { daysToAdd: 7, time: { hours: 8, minutes: 0 } };

    const formatToLocalISO = (date) => {
        const time = date.getTime()
        return formatEpochToISO(time);
    };
    const formatEpochToISO = (epochMillis) => {
        const date = new Date(epochMillis < 1e12 ? epochMillis * 1000 : epochMillis);
        return date.toISOString();
    };

    const formatDateForDisplay = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-US', {
            weekday: 'short',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        });
    };

    const getFormattedDate = (dateOption) => {
        if (dateOption && typeof dateOption === 'object' && 'daysToAdd' in dateOption) {
            const date = new Date();
            date.setDate(date.getDate() + dateOption.daysToAdd);
            date.setHours(dateOption.time.hours, dateOption.time.minutes, 0, 0);
            return date;
        }
        return dateOption;
    };

    const handleReminderSelection = (dateOption) => {
        const date = getFormattedDate(dateOption);
        const formattedDate = formatToLocalISO(date);
        updateSelectedReminderDateRedux(formattedDate);
        updateSelectedReminderDate(formattedDate);
        handleUpdateTask(formattedDate, 'reminder', taskItem);
        if (myDayChecked) {
            handleUpdateTask(formattedDate, 'myDay', taskItem);
            updateMyDayDate(formattedDate);
        }
        if (deadlineChecked) {
            updateDeadlineDate(formattedDate);
            handleUpdateTask(formattedDate, 'deadline', taskItem);
        }
        setShowReminderMeDropdown(false);
        setIsReminder(true);
    };

    useEffect(() => {
        if (selectedReminderDate) {
            const date = new Date(selectedReminderDate);
            setCustomDate(date);
        }
    }, [selectedReminderDate]);

    const handleDateChange = (date) => {
        setCustomDate(date);
        setShowDatePicker(false);
        handleReminderSelection(date);
        setShowReminderMeDropdown(false);
    };

    const handleDateOnlyChange = (date) => {
        const next = customDate ? new Date(customDate) : new Date();
        next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        if (!customDate) {
            next.setHours(12, 0, 0, 0);
        }
        setCustomDate(next);
    };

    const toggleDatePicker = () => {
        if (!showDatePicker) {
            setCustomDate(customDate || new Date());
        }
        setShowDatePicker(!showDatePicker);
    };

    const handleClearReminder = () => {
        updateSelectedReminderDateRedux(null);
        updateSelectedReminderDate(null);
        handleUpdateTask(null, 'reminder', taskItem);
        setIsReminder(false);
    };

    const presetOptions = [
        { key: 'today', label: 'Later today', icon: '☀️', option: today },
        { key: 'tomorrow', label: 'Tomorrow', icon: '🌙', option: tomorrow },
        { key: 'twoDays', label: 'In two days', icon: '🕒', option: inTwoDays },
        { key: 'nextWeek', label: 'Next week', icon: '📅', option: nextWeek },
    ];

    return (
        <div
            className={`reminder-dropdown ${clickedBellIcon ? 'reminder-dropdown--bell' : fixedPosition ? 'reminder-dropdown--fixed' : ''}`}
            style={
                fixedPosition
                    ? { top: fixedPosition.top, right: fixedPosition.right, width: fixedPosition.width || Math.min(300, window.innerWidth - 16), maxHeight: window.innerHeight - fixedPosition.top - 8 }
                    : clickedBellIcon
                        ? { top: '50px', right: '2vw', minWidth: 320 }
                        : { minWidth: 320 }
            }
        >
            <div className={`reminder-dropdown__card ${showDatePicker ? 'reminder-dropdown__card--expanded' : ''}`}>
                <div className="reminder-dropdown__header">
                    <span className="reminder-dropdown__title">Reminder...</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button type="button" onClick={handleClearReminder} className="reminder-dropdown__clear">Clear</button>
                        <button
                            type="button"
                            onClick={() => setShowReminderMeDropdown(false)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}
                            title="Close"
                        >✕</button>
                    </div>
                </div>

                <div className="reminder-dropdown__note-wrap">
                    <textarea
                        placeholder="Add an optional note or next step"
                        className="reminder-dropdown__note"
                        rows={1}
                        value={note}
                        onChange={handleReminderChange}
                    />
                </div>

                <ul className="reminder-dropdown__options">
                    {presetOptions.map(({ key, label, icon, option }) => (
                        <li key={key}>
                            <button
                                type="button"
                                className="reminder-dropdown__option"
                                onClick={() => handleReminderSelection(option)}
                            >
                                <span className="reminder-dropdown__option-left">
                                    <span className="reminder-dropdown__option-icon" aria-hidden="true">
                                        {icon}
                                    </span>
                                    <span className="reminder-dropdown__option-label">{label}</span>
                                </span>
                                <span className="reminder-dropdown__option-time">
                                    {formatDateForDisplay(getFormattedDate(option))}
                                </span>
                            </button>
                        </li>
                    ))}
                    <li className={showDatePicker ? 'reminder-dropdown__pick-row--open' : ''}>
                        <button
                            type="button"
                            className={`reminder-dropdown__option ${showDatePicker ? 'reminder-dropdown__option--active' : ''}`}
                            onClick={toggleDatePicker}
                            aria-expanded={showDatePicker}
                        >
                            <span className="reminder-dropdown__option-left">
                                <span className="reminder-dropdown__option-icon" aria-hidden="true">
                                    📆
                                </span>
                                <span className="reminder-dropdown__option-label">Pick a date</span>
                            </span>
                        </button>
                    </li>
                </ul>

                {showDatePicker && (
                    <ReminderDateTimePicker
                        customDate={customDate}
                        setCustomDate={setCustomDate}
                        onDateOnlyChange={handleDateOnlyChange}
                        onSave={() => handleDateChange(customDate)}
                        onToday={() => setCustomDate(new Date())}
                        onClear={() => setCustomDate(null)}
                    />
                )}

                <div className="reminder-dropdown__footer">
                    <label className="reminder-dropdown__checkbox-label">
                        <input
                            type="checkbox"
                            className="reminder-dropdown__checkbox"
                            checked={myDayChecked}
                            onChange={(e) => setMyDayChecked(e.target.checked)}
                        />
                        Add this item to My Day plan
                    </label>
                    <label className="reminder-dropdown__checkbox-label">
                        <input
                            type="checkbox"
                            className="reminder-dropdown__checkbox"
                            checked={deadlineChecked}
                            onChange={(e) => setDeadlineChecked(e.target.checked)}
                        />
                        Also update the deadline date
                    </label>
                </div>
            </div>
        </div>
    );
};

export default ReminderMe;
